import { forwardRef, PropsWithoutRef, Suspense, useImperativeHandle } from 'react';
import {
    createZodFormResolver,
    DateTimeUtils,
    FormHiddenInput,
    FormTextareaField,
    lt,
    showSuccessAlert,
    useTimezone,
    useFormControls,
    showInfoAlert,
} from '@mews/framework';
import { DateField } from '@mews-commander/form';
import { localizeCurrencyValue } from '@mews-commander/utils';
import { Loader } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { PaymentCardFormat, PreauthorizationStatus } from '@mews-commander/data-access';
import { Card, Stack } from '@optimus-web/core';
import { PaymentFormProps } from '../types';
import { useDefaultFormContext } from '../useDefaultFormValues';
import {
    useTokenizeCard,
    useInvalidatePaymentMethodsTableRecordsQuery,
    useInvalidatePreauthorizationTableRecordQuery,
    useGetPaymentMethodsSuspense,
} from '../../../../queries';
import { GatewayPaymentFormField } from '../GatewayPaymentForm/schema';
import { useAddGatewayPreauthorization, useGetSupportedGatewayCardTypesSuspense } from '../queries';
import { PaymentAmountSection } from '../../../common/PaymentAmountSection';
import { isNewCard } from '../../../../utils';
import { PaymentCommonSection } from '../../../common/PaymentCommonSection';
import { PaymentMethodSelectField } from '../PaymentMethodSelectField/PaymentMethodSelectField';
import { useGetPreauthorizationDateTimesUtc } from '../hooks';
import { ProcessPaymentFormField } from '../ProcessPaymentFormSchema';
import { TokenizeCardFormFields } from '../TokenizeCardForm/TokenizeCardFormFields';
import { TokenizeCardFormField } from '../TokenizeCardForm/schema';
import {
    createGatewayPreauthorizationForNewCardSchema,
    createGatewayPreauthorizationForSelectedCardSchema,
    isGatewayPreauthorizationForNewCardFormValues,
    GatewayPreauthorizationFormField,
    GatewayPreauthorizationFormValues,
} from './schema';

export const GatewayPreauthorizationForm = forwardRef(
    ({ accountId, onSuccess, onSubmitting, onSubmitted }: PropsWithoutRef<PaymentFormProps>, ref) => {
        const timezone = useTimezone();
        const invalidatePaymentMethods = useInvalidatePaymentMethodsTableRecordsQuery();
        const invalidatePreauthorizationTableRecordQuery = useInvalidatePreauthorizationTableRecordQuery();

        const {
            data: { paymentCards },
        } = useGetPaymentMethodsSuspense({ accountId });
        const {
            data: { supportedPaymentCardTypes },
        } = useGetSupportedGatewayCardTypesSuspense();

        const tokenizeCard = useTokenizeCard({ accountId });
        const addGatewayPreauthorization = useAddGatewayPreauthorization({ accountId });

        const { defaultFormValues: defaultValues } = useDefaultFormContext();

        const { defaultDateTimeUtc, maxDateTimeUtc, minDateTimeUtc } = useGetPreauthorizationDateTimesUtc();

        const { renderFormContent, submit, hasDirtyFields, watch, setValue } =
            useFormControls<GatewayPreauthorizationFormValues>({
                onSubmit: async (formValues) => {
                    try {
                        onSubmitting?.();
                        const { paymentMethodId, amount, currencyCode } = formValues;
                        let creditCardId = paymentMethodId;
                        let cardLastDigits = paymentCards?.find(({ id }) => id === creditCardId)?.lastDigits;

                        if (isGatewayPreauthorizationForNewCardFormValues(formValues)) {
                            const { lastDigits, id } = await tokenizeCard(formValues);

                            showSuccessAlert({
                                description: lt('CardCreatedSuccessMessage', { cardLastDigits: lastDigits ?? '' }),
                            });

                            creditCardId = id;
                            cardLastDigits = lastDigits;
                            setValue(GatewayPaymentFormField.PaymentMethodId, id);
                            invalidatePaymentMethods({ customerId: accountId, isPreauthorization: true });
                        }

                        const { state } = await addGatewayPreauthorization({
                            ...formValues,
                            paymentMethodId: creditCardId,
                        });

                        switch (state) {
                            case PreauthorizationStatus.Preauthorized:
                                showSuccessAlert({
                                    description: lt('CardPreauthorizationSuccessMessage', {
                                        cardLastDigits: cardLastDigits ?? '',
                                        amount: localizeCurrencyValue({ value: amount, currency: currencyCode }),
                                    }),
                                });
                                break;
                            case PreauthorizationStatus.Verifying:
                                showInfoAlert({
                                    description: lt('VerifyingPreauthorizationResultMessage'),
                                });
                                break;
                            default:
                                break;
                        }

                        invalidatePreauthorizationTableRecordQuery({ accountId });

                        onSuccess?.();
                    } finally {
                        onSubmitted?.();
                    }
                },
                defaultValues: {
                    ...defaultValues,
                    [GatewayPaymentFormField.PaymentMethodId]: defaultValues.paymentMethodId ?? undefined,
                    [GatewayPreauthorizationFormField.ExpirationUtc]: defaultDateTimeUtc.value,
                },
                resolver: (values) => {
                    const schema = isNewCard(values[GatewayPaymentFormField.PaymentMethodId])
                        ? createGatewayPreauthorizationForNewCardSchema(supportedPaymentCardTypes)
                        : createGatewayPreauthorizationForSelectedCardSchema();
                    return createZodFormResolver(schema)(values);
                },
            });

        useImperativeHandle(
            ref,
            () => ({
                submit,
                hasDirtyFields,
            }),
            [submit, hasDirtyFields]
        );

        return renderFormContent(
            <Suspense fallback={<Loader />}>
                <Stack spacing="300" vertical>
                    <PaymentCommonSection hideLinkPaymentToPlan />
                    <PaymentMethodSelectField
                        required
                        name={GatewayPaymentFormField.PaymentMethodId}
                        label={lt('PaymentCard')}
                        helper={lt('ProcessPaymentMewsPaymentsCardPaymentCardSelectionHint')}
                        accountId={accountId}
                        isExternal={false}
                    />
                    {isNewCard(watch(GatewayPaymentFormField.PaymentMethodId)) ? (
                        <Card padding="300" outline>
                            <Suspense fallback={<Loader />}>
                                <TokenizeCardFormFields />
                                <FormHiddenInput
                                    name={TokenizeCardFormField.PaymentCardFormat}
                                    defaultValue={PaymentCardFormat.Physical}
                                />
                            </Suspense>
                        </Card>
                    ) : null}
                    <PaymentAmountSection />
                    <DateField
                        required
                        name={GatewayPreauthorizationFormField.ExpirationUtc}
                        label={lt(TextKey.ProcessPaymentMewsPreauthorizationExpirationLabel)}
                        mode="utc"
                        minDate={DateTimeUtils.utcToStructuredDate(minDateTimeUtc.value, timezone)}
                        maxDate={DateTimeUtils.utcToStructuredDate(maxDateTimeUtc.value, timezone)}
                    />
                    <FormTextareaField name={ProcessPaymentFormField.Notes} label={lt('Notes')} rows={1} />
                </Stack>
            </Suspense>
        );
    }
);
